import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import TaxonomySelector from '../components/TaxonomySelector';
import { mergePathwayRoots, effectivePrimaryPathwayId, effectiveAdditionalPathwayIds, filteredPathwaysForScope } from '../utils/pathwayTaxonomy';
import PathwayScopeToggle from '../components/PathwayScopeToggle';
import UploadMediaOpenInNewTabLink from '../components/UploadMediaOpenInNewTabLink';
import AssignmentAssistant from '../components/AssignmentAssistant';
import './AssignmentBuilder.css';

const initialAssignment = {
  title: '',
  gradingresulttype: 'Points',
  maxpoints: 0,
  hours: 1,
  concentration: '',
  otherconcentrations: [],
  taxonomy: [],
  initialdirections: [],
  questions: [],
};

const directionTypes = [
  { value: 'richText', label: 'Rich Text' },
  { value: 'image', label: 'Image' },
  { value: 'video', label: 'Video' },
];

const questionTypes = [
  { value: 'directions', label: 'Directions' },
  { value: 'singleChoice', label: 'Single Choice' },
  { value: 'multipleChoice', label: 'Multiple Choice' },
  { value: 'openAnswer', label: 'Open Answer' },
  { value: 'upload', label: 'Upload' },
  { value: 'video', label: 'Video' },
];

const gradingTypes = [
  { value: 'Points', label: 'Points' },
  { value: 'None', label: 'None' },
];

// SVG icons
const PencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
    <path d="M14.69 2.86a2.1 2.1 0 0 1 2.97 2.97l-1.06 1.06-2.97-2.97 1.06-1.06zm-2.12 2.12l2.97 2.97-8.49 8.49c-.13.13-.29.22-.47.25l-3.18.53c-.41.07-.77-.29-.7-.7l.53-3.18c.03-.18.12-.34.25-.47l8.49-8.49z" fill="#444"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ verticalAlign: 'middle' }}>
    <path d="M3 5h14M7 5V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1m2 0v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5m3 4v6m4-6v6" stroke="#c00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M18 15l-6-6-6 6" />
  </svg>
);
const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

function AssignmentBuilder({ userData }) {
  const [searchParams] = useSearchParams();
  const command = searchParams.get('command');
  const assignmentId = searchParams.get('id');
  const isEditMode = command === 'edit' && assignmentId;
  const overrideSubmissionsRestriction = searchParams.get('override_submissions_restriction') === 'true';
  
  const [assignment, setAssignment] = useState(initialAssignment);
  const [concentrations, setConcentrations] = useState([]);
  const [courseSubjects, setCourseSubjects] = useState([]);
  const [selectedConcentrations, setSelectedConcentrations] = useState([]);
  const [taxonomyItems, setTaxonomyItems] = useState([]);
  const [aiCapabilities, setAiCapabilities] = useState(null);

  const pathwayRoots = useMemo(
    () => mergePathwayRoots(concentrations, courseSubjects),
    [concentrations, courseSubjects]
  );

  const hasCtePathways = useMemo(
    () => pathwayRoots.some((p) => p.pathwayKind === 'concentration'),
    [pathwayRoots]
  );
  const hasSubjectPathways = useMemo(
    () => pathwayRoots.some((p) => p.pathwayKind === 'course_subject'),
    [pathwayRoots]
  );

  const [primaryScopeFilter, setPrimaryScopeFilter] = useState('concentration');
  const [taxonomyScopeFilter, setTaxonomyScopeFilter] = useState('concentration');

  useEffect(() => {
    if (!assignment.concentration || pathwayRoots.length === 0) return;
    const row = pathwayRoots.find((p) => p.id === assignment.concentration);
    if (row) {
      setPrimaryScopeFilter(row.pathwayKind);
      setTaxonomyScopeFilter(row.pathwayKind);
    }
  }, [assignment.concentration, pathwayRoots]);

  // Default scope tabs for new assignments: Subjects if user has no INSTRUCTOR_CONCENTRATION links; else Concentrations.
  useEffect(() => {
    if (pathwayRoots.length === 0) return;
    if (assignment.concentration) return;

    if (!hasCtePathways && hasSubjectPathways) {
      setPrimaryScopeFilter('course_subject');
      setTaxonomyScopeFilter('course_subject');
      return;
    }
    if (hasCtePathways && !hasSubjectPathways) {
      setPrimaryScopeFilter('concentration');
      setTaxonomyScopeFilter('concentration');
      return;
    }
    if (!hasCtePathways && !hasSubjectPathways) return;

    const hasInstrConc = userData?.has_instructor_concentration_relationships === true;
    const scope = hasInstrConc ? 'concentration' : 'course_subject';
    setPrimaryScopeFilter(scope);
    setTaxonomyScopeFilter(scope);
  }, [pathwayRoots, assignment.concentration, hasCtePathways, hasSubjectPathways, userData]);

  const primaryOptionsForScope = useMemo(
    () => filteredPathwaysForScope(pathwayRoots, primaryScopeFilter),
    [pathwayRoots, primaryScopeFilter]
  );

  const taxonomyPathwayOptions = useMemo(
    () =>
      filteredPathwaysForScope(pathwayRoots, taxonomyScopeFilter, {
        alwaysIncludeId: assignment.concentration || null
      }),
    [pathwayRoots, taxonomyScopeFilter, assignment.concentration]
  );

  const handlePrimaryScopeChange = (scope) => {
    setPrimaryScopeFilter(scope);
    const stillValid = pathwayRoots.some(
      (p) => p.id === assignment.concentration && p.pathwayKind === scope
    );
    if (!stillValid) {
      setAssignment((a) => ({ ...a, concentration: '' }));
    }
  };
  
  // Loading and error states for critical data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [concentrationsLoaded, setConcentrationsLoaded] = useState(false);
  const [assignmentLoaded, setAssignmentLoaded] = useState(false);
  const [criticalDataError, setCriticalDataError] = useState(null);
  const [directionDraft, setDirectionDraft] = useState({ 
    element: 'richText', 
    value: '',
    imageUrl: '',
    imageWidth: '',
    imageHeight: ''
  });
  const [questionDraft, setQuestionDraft] = useState({ 
    type: 'directions', 
    gradingType: 'Points', 
    label: '', 
    required: true, 
    choices: [], 
    answer: '', 
    maxPoints: 0, 
    responseType: '' 
  });
  const [choiceDraft, setChoiceDraft] = useState('');
  const [editingDirectionId, setEditingDirectionId] = useState(null);
  const [directionModalOpen, setDirectionModalOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [questionDockOpen, setQuestionDockOpen] = useState(false);
  const [choiceEditIndex, setChoiceEditIndex] = useState(null);
  const [choiceEditValue, setChoiceEditValue] = useState('');
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTab, setPreviewTab] = useState('user'); // 'user' | 'json'

  const openPreviewModal = useCallback((tab = 'user') => {
    setPreviewTab(tab);
    setPreviewModalOpen(true);
  }, []);

  const closeQuestionDock = useCallback(() => {
    setQuestionDockOpen(false);
    setEditingQuestionId(null);
    setChoiceEditIndex(null);
    setChoiceEditValue('');
    setChoiceDraft('');
    setQuestionDraft({
      type: 'directions',
      gradingType: assignment.gradingresulttype === 'Points' ? 'Points' : 'None',
      label: '',
      required: true,
      choices: [],
      answer: '',
      maxPoints: 0,
      responseType: '',
    });
  }, [assignment.gradingresulttype]);

  const openQuestionDockForAdd = useCallback(() => {
    setChoiceEditIndex(null);
    setChoiceEditValue('');
    setChoiceDraft('');
    setEditingQuestionId(null);
    setQuestionDraft({
      type: 'directions',
      gradingType: assignment.gradingresulttype === 'Points' ? 'Points' : 'None',
      label: '',
      required: true,
      choices: [],
      answer: '',
      maxPoints: 0,
      responseType: '',
    });
    setQuestionDockOpen(true);
  }, [assignment.gradingresulttype]);

  const closeDirectionModal = useCallback(() => {
    setDirectionModalOpen(false);
    setEditingDirectionId(null);
    setDirectionDraft({ element: 'richText', value: '', imageUrl: '', imageWidth: '', imageHeight: '' });
  }, []);

  const openDirectionModalForAdd = useCallback(() => {
    setEditingDirectionId(null);
    setDirectionDraft({ element: 'richText', value: '', imageUrl: '', imageWidth: '', imageHeight: '' });
    setDirectionModalOpen(true);
  }, []);
  
  // Upload functionality state
  const [imageUploading, setImageUploading] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [videoUploadError, setVideoUploadError] = useState('');
  const [uploadsLoading, setUploadsLoading] = useState(false);
  const [userUploads, setUserUploads] = useState([]);
  const [showUploadsModal, setShowUploadsModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [lastSubmissionResult, setLastSubmissionResult] = useState(null);
  const [hasSubmissions, setHasSubmissions] = useState(false);
  const submissionsRestrictionActive = hasSubmissions && !overrideSubmissionsRestriction;

  // Refs for scrolling
  const directionEditorRef = useRef(null);
  const questionEditorRef = useRef(null);

  const scrollToAbSection = useCallback((sectionId) => {
    try {
      const el = document.getElementById(sectionId);
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const absoluteY = rect.top + window.pageYOffset;
      const topOffset = 104;
      const targetY = Math.max(absoluteY - topOffset, 0);
      window.scrollTo({ top: targetY, behavior: 'smooth' });
      el.classList.add('highlight-flash');
      setTimeout(() => {
        el.classList.remove('highlight-flash');
      }, 1500);
    } catch (e) {
      /* no-op */
    }
  }, []);

  const abSectionNavItems = useMemo(
    () => [
      { id: 'ab-section-setup', icon: '⚙', label: 'Setup' },
      { id: 'ab-section-taxonomy', icon: '🏷', label: 'Taxonomy' },
      { id: 'ab-section-directions', icon: '▤', label: 'Directions' },
      { id: 'ab-section-questions', icon: '❓', label: 'Questions' },
      { id: 'ab-section-preview', icon: '👁', label: 'Preview' },
    ],
    []
  );

  // Prevent scrolling the page behind full-screen load / error / modals
  useEffect(() => {
    const shouldLock =
      loading ||
      Boolean(criticalDataError) ||
      Boolean(error) ||
      (isEditMode && submissionsRestrictionActive) ||
      questionDockOpen ||
      directionModalOpen;
    if (!shouldLock) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [loading, criticalDataError, error, isEditMode, submissionsRestrictionActive, questionDockOpen, directionModalOpen]);

  useEffect(() => {
    if (!questionDockOpen && !directionModalOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (questionDockOpen) closeQuestionDock();
      if (directionModalOpen) closeDirectionModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [questionDockOpen, directionModalOpen, closeQuestionDock, closeDirectionModal]);

  // Fetch existing assignment data if in edit mode
  useEffect(() => {
    const fetchExistingAssignment = async () => {
      if (!isEditMode || !assignmentId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }
        
        const response = await axios.get(`/api/assignments/${assignmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.data || !response.data.assignment) {
          throw new Error('Invalid response format from server');
        }
        

        
        // Check for existing submissions
        const submissionsResponse = await axios.get(`/api/assignmentsubmissions/by-assignment/${assignmentId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (submissionsResponse.data.submissionCount > 0) {
          // Mark that this assignment has submissions
          setHasSubmissions(true);
        }
        
        const existingAssignment = response.data.assignment;
        
        // Transform the database data to match our component structure
        const primaryId = effectivePrimaryPathwayId(existingAssignment) || '';
        const additionalIds = effectiveAdditionalPathwayIds(existingAssignment);

        const transformedAssignment = {
          title: existingAssignment.title || '',
          gradingresulttype: existingAssignment.gradingresulttype || 'Points',
          maxpoints: existingAssignment.maxpoints || 0,
          hours: existingAssignment.hours || 1,
          concentration: primaryId,
          otherconcentrations: additionalIds,
          taxonomy: existingAssignment.taxonomy || [],
          initialdirections: existingAssignment.initialdirections || [],
          questions: existingAssignment.questions || [],
        };
        
        // Parse JSON fields if they're strings
        if (typeof transformedAssignment.initialdirections === 'string') {
          transformedAssignment.initialdirections = JSON.parse(transformedAssignment.initialdirections);
        }
        if (typeof transformedAssignment.questions === 'string') {
          transformedAssignment.questions = JSON.parse(transformedAssignment.questions);
        }
        if (typeof transformedAssignment.otherconcentrations === 'string') {
          transformedAssignment.otherconcentrations = JSON.parse(transformedAssignment.otherconcentrations);
        }
        if (typeof transformedAssignment.taxonomy === 'string') {
          transformedAssignment.taxonomy = JSON.parse(transformedAssignment.taxonomy);
        }
        
        // Add _id to each direction and question for editing
        transformedAssignment.initialdirections = transformedAssignment.initialdirections.map(d => ({
          ...d,
          _id: d._id || uuidv4()
        }));
        transformedAssignment.questions = transformedAssignment.questions.map(q => ({
          ...q,
          _id: q._id || uuidv4()
        }));
        
        setAssignment(transformedAssignment);
        
        // Initialize selectedConcentrations with the otherconcentrations from the database
        console.log('🔍 Initializing selectedConcentrations with:', transformedAssignment.otherconcentrations);
        setSelectedConcentrations(additionalIds || []);
        
        setAssignmentLoaded(true);
        
      } catch (error) {
        console.error('Error fetching existing assignment:', error);
        setError(error.message || 'Failed to load existing assignment. Please refresh the page.');
      } finally {
        // Only set loading to false if concentrations are also loaded
        if (concentrationsLoaded) {
          setLoading(false);
        }
      }
    };

    fetchExistingAssignment();
  }, [isEditMode, assignmentId, concentrationsLoaded]);

  // Fetch concentrations on component mount
  useEffect(() => {
    const fetchConcentrations = async () => {
      try {
        // Only set loading to true if we're not already loading assignment data
        if (!isEditMode) {
          setLoading(true);
        }
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }
        
        const [response, subjectsResponse] = await Promise.all([
          axios.get('/api/concentrations', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/api/taxonomy/course-subjects', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        if (!response.data || !response.data.concentrations) {
          throw new Error('Invalid response format from server');
        }
        
        const concList = response.data.concentrations;
        const subjList = subjectsResponse.data?.items || [];
        setConcentrations(concList);
        setCourseSubjects(subjList);
        setConcentrationsLoaded(true);
        
        if (concList.length === 0 && subjList.length === 0) {
          setCriticalDataError('No CTE pathways or course subjects found. Please contact your administrator.');
          return;
        }
        
        // Fetch taxonomy items
        const taxonomyResponse = await axios.get('/api/taxonomy/all', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTaxonomyItems(taxonomyResponse.data.items || []);
        
      } catch (error) {
        console.error('Error fetching concentrations:', error);
        setCriticalDataError(error.message || 'Failed to load concentrations. Please refresh the page.');
      } finally {
        // Only set loading to false if we're not in edit mode or if assignment is already loaded
        if (!isEditMode || assignmentLoaded) {
          setLoading(false);
        }
      }
    };

    fetchConcentrations();
  }, [isEditMode, assignmentLoaded]);

  useEffect(() => {
    const fetchAiCapabilities = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get('/api/ai/capabilities', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAiCapabilities(res.data);
      } catch (err) {
        console.warn('Failed to load assignment assistant capabilities:', err);
      }
    };
    fetchAiCapabilities();
  }, []);

  const refreshTaxonomyItems = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const [taxonomyResponse, concResponse, subjectsResponse] = await Promise.all([
      axios.get('/api/taxonomy/all', {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get('/api/concentrations', {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get('/api/taxonomy/course-subjects', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    setTaxonomyItems(taxonomyResponse.data.items || []);
    if (concResponse.data?.concentrations) {
      setConcentrations(concResponse.data.concentrations);
    }
    if (subjectsResponse.data?.items) {
      setCourseSubjects(subjectsResponse.data.items);
    }
  }, []);

  // Calculate total maxpoints from questions
  const calculateMaxPoints = (questions) => {
    return questions
      .filter(q => q.gradingType === 'Points' && ['singleChoice', 'multipleChoice', 'openAnswer', 'upload'].includes(q.type))
      .reduce((sum, q) => sum + (q.maxPoints || 0), 0);
  };

  // Check if this is a survey assignment
  const isSurveyAssignment = assignment.gradingresulttype === 'Survey';

  // Update maxPoints when questions change
  useEffect(() => {
    if (assignment.gradingresulttype === 'Points') {
      const totalPoints = calculateMaxPoints(assignment.questions);
      setAssignment(prev => ({ ...prev, maxpoints: totalPoints }));
    }
  }, [assignment.questions, assignment.gradingresulttype]);

  // Assignment type change
  const handleTypeChange = (e) => {
    const type = e.target.value;
    const newGradingType = type === 'Points' ? 'Points' : 'None';
    setAssignment((prev) => ({
      ...prev,
      gradingresulttype: type,
      maxpoints: type === 'Points' ? calculateMaxPoints(prev.questions) : null,
      questions: prev.questions.map(q => ({
        ...q,
        gradingType: (q.type === 'directions' || q.type === 'video') ? 'None' : newGradingType,
        maxPoints: type === 'Survey' ? null : (q.maxPoints == null ? 0 : q.maxPoints)
      }))
    }));
    // Update question draft grading type
    setQuestionDraft(prev => ({ ...prev, gradingType: newGradingType }));
  };

  // Add or update initial direction
  const addOrUpdateDirection = () => {
    let value = '';
    if (directionDraft.element === 'richText') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    } else if (directionDraft.element === 'image') {
      if (!directionDraft.imageUrl || !directionDraft.imageWidth || !directionDraft.imageHeight) return;
      value = `<img src="${directionDraft.imageUrl}" width="${directionDraft.imageWidth}" height="${directionDraft.imageHeight}" alt="Image">`;
    } else if (directionDraft.element === 'video') {
      if (!directionDraft.value.trim()) return;
      value = directionDraft.value;
    }
    if (editingDirectionId) {
      setAssignment(prev => ({
        ...prev,
        initialdirections: prev.initialdirections.map(d =>
          d._id === editingDirectionId ? { ...d, element: directionDraft.element, value } : d
        )
      }));
      setEditingDirectionId(null);
    } else {
      setAssignment(prev => ({
        ...prev,
        initialdirections: [
          ...prev.initialdirections,
          { _id: uuidv4(), element: directionDraft.element, value },
        ],
      }));
    }
    setDirectionDraft({ 
      element: 'richText', 
      value: '', 
      imageUrl: '', 
      imageWidth: '', 
      imageHeight: '' 
    });
    setDirectionModalOpen(false);
  };

  // Edit initial direction
  const editDirection = (d) => {
    if (d.element === 'image') {
      // Parse image tag for url, width, height
      const match = d.value.match(/src=\"([^\"]+)\".*width=\"(\d+)\".*height=\"(\d+)\"/);
      setDirectionDraft({
        element: 'image',
        value: '',
        imageUrl: match ? match[1] : '',
        imageWidth: match ? match[2] : '',
        imageHeight: match ? match[3] : ''
      });
    } else {
      setDirectionDraft({
        element: d.element,
        value: d.value,
        imageUrl: '',
        imageWidth: '',
        imageHeight: ''
      });
    }
    setEditingDirectionId(d._id);
    setDirectionModalOpen(true);
  };

  // Helper to check if label has text
  const labelHasText = (label) => {
    if (!label) return false;
    // Remove HTML tags and whitespace
    const text = label.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
    return text.length > 0;
  };

  // Add or update question
  const addOrUpdateQuestion = () => {
    if (!questionDraft.label.trim()) return;
    let q = { 
      ...questionDraft, 
      gradingType: (questionDraft.type === 'directions' || questionDraft.type === 'video') ? 'None' : (assignment.gradingresulttype === 'Points' ? 'Points' : 'None'),
      required: true
    };
    // Set maxPoints to null for video and directions
    if (q.type === 'video' || q.type === 'directions') q.maxPoints = null;
    if (q.type === 'singleChoice' && !q.answer) q.answer = '';
    if (q.type === 'multipleChoice' && !Array.isArray(q.answer)) q.answer = [];
    if (q.type === 'openAnswer' || q.type === 'upload') q.answer = undefined;
    if (q.type === 'video' && !q.answer) q.answer = '';
    if (q.type === 'directions') q.answer = undefined;
    if (q.type !== 'singleChoice' && q.type !== 'multipleChoice') q.choices = undefined;
    if (q.type !== 'openAnswer') q.responseType = undefined;
    if (editingQuestionId) {
      setAssignment(prev => ({
        ...prev,
        questions: prev.questions.map(qq =>
          qq._id === editingQuestionId ? { ...q, _id: editingQuestionId } : qq
        )
      }));
      setEditingQuestionId(null);
    } else {
      setAssignment(prev => ({
        ...prev,
        questions: [...prev.questions, { ...q, _id: uuidv4() }],
      }));
    }
    setQuestionDraft({ 
      type: 'directions', 
      gradingType: assignment.gradingresulttype === 'Points' ? 'Points' : 'None', 
      label: '', 
      required: true, 
      choices: [], 
      answer: '', 
      maxPoints: 0, 
      responseType: '' 
    });
    setQuestionDockOpen(false);
  };

  // Edit question
  const editQuestion = (q) => {
    setChoiceEditIndex(null);
    setChoiceEditValue('');
    setQuestionDraft({
      ...q,
      gradingType: assignment.gradingresulttype === 'Points' ? 'Points' : 'None',
      required: true,
      choices: q.choices || [],
      answer: q.answer || (q.type === 'multipleChoice' ? [] : ''),
      responseType: q.responseType || '',
      maxPoints: q.maxPoints || 0
    });
    setEditingQuestionId(q._id);
    setQuestionDockOpen(true);
  };

  // Add choice to question draft
  const addChoice = () => {
    if (!choiceDraft.trim()) return;
    setQuestionDraft((prev) => ({ ...prev, choices: [...(prev.choices || []), choiceDraft.trim()] }));
    setChoiceDraft('');
  };

  // Remove direction
  const removeDirection = (id) => {
    setAssignment((prev) => ({ ...prev, initialdirections: prev.initialdirections.filter(d => d._id !== id) }));
    // Clear editor if the deleted direction was being edited
    if (editingDirectionId === id) {
      setEditingDirectionId(null);
      setDirectionDraft({ element: 'richText', value: '', imageUrl: '', imageWidth: '', imageHeight: '' });
      setDirectionModalOpen(false);
    }
  };

  // Remove question
  const removeQuestion = (id) => {
    setAssignment((prev) => ({ ...prev, questions: prev.questions.filter(q => q._id !== id) }));
    // Clear editor if the deleted question was being edited
    if (editingQuestionId === id) {
      setEditingQuestionId(null);
      setQuestionDockOpen(false);
      setQuestionDraft({ 
        type: 'directions', 
        gradingType: assignment.gradingresulttype === 'Points' ? 'Points' : 'None', 
        label: '', 
        required: true, 
        choices: [], 
        answer: '', 
        maxPoints: 0, 
        responseType: '' 
      });
    }
  };

  // Helper to render image from img tag
  const renderImageFromTag = (imgTag) => {
    const match = imgTag.match(/src=\"([^\"]+)\".*width=\"(\d+)\".*height=\"(\d+)\"/);
    if (match) {
      return <img src={match[1]} width={match[2]} height={match[3]} alt="Image" style={{ border: '1px solid #ccc' }} />;
    }
    return null;
  };

  // Helper to render video from URL
  const renderVideoFromUrl = (url) => {
    // Try to embed Vimeo/YouTube, fallback to HTML5 video
    if (/vimeo\.com/.test(url)) {
      const id = url.split('/').pop();
      return (
        <div className="video-container">
          <iframe src={`https://player.vimeo.com/video/${id}`} width="800" height="450" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Vimeo Video" />
        </div>
      );
    }
    if (/youtube\.com|youtu\.be/.test(url)) {
      let id = url;
      if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1];
      else if (url.includes('v=')) id = url.split('v=')[1].split('&')[0];
      return (
        <div className="video-container">
          <iframe width="800" height="450" src={`https://www.youtube.com/embed/${id}`} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen title="YouTube Video" />
        </div>
      );
    }
    // Fallback: HTML5 video
    return (
      <div className="video-container">
        <video src={url} width="800" height="450" controls />
      </div>
    );
  };

  // Handle image file upload (uses presigned URL for direct S3 upload - bypasses proxy size limits)
  const handleImageFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      setImageUploadError('Please select an image file.');
      return;
    }

    if (!userData || !userData.account_id) {
      setImageUploadError('User data not available. Please refresh the page.');
      return;
    }

    setImageUploading(true);
    setImageUploadError('');
    
    try {
      const uploadId = uuidv4();
      const token = localStorage.getItem('token');
      
      // Get presigned URL (metadata only - no file through proxy)
      const response = await axios.post('/api/upload-url', {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        uploadId: uploadId,
        details: 'assignment builder - direction'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload file directly to S3 using presigned URL (bypasses proxy)
      const uploadResponse = await fetch(response.data.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const url = response.data.uploadRecord.link;
      // Update direction draft with the uploaded image
      setDirectionDraft(prev => ({
        ...prev,
        element: 'image',
        value: `<img src="${url}" width="250" height="250" alt="Image">`,
        imageUrl: url,
        imageWidth: '250',
        imageHeight: '250'
      }));

    } catch (err) {
      console.error('Error uploading image:', err);
      setImageUploadError('Failed to upload image.');
    } finally {
      setImageUploading(false);
    }
  };

  // Handle video file upload (uses presigned URL for direct S3 upload - bypasses proxy size limits)
  const handleVideoFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check if file is a video
    if (!file.type.startsWith('video/')) {
      setVideoUploadError('Please select a video file.');
      return;
    }

    if (!userData || !userData.account_id) {
      setVideoUploadError('User data not available. Please refresh the page.');
      return;
    }

    setVideoUploading(true);
    setVideoUploadError('');
    
    try {
      const uploadId = uuidv4();
      const token = localStorage.getItem('token');
      
      // Get presigned URL (metadata only - no file through proxy)
      const response = await axios.post('/api/upload-url', {
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
        uploadId: uploadId,
        details: 'assignment builder - direction'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Upload file directly to S3 using presigned URL (bypasses proxy)
      const uploadResponse = await fetch(response.data.presignedUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type }
      });
      
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed: ${uploadResponse.statusText}`);
      }

      const url = response.data.uploadRecord.link;
      // Update direction draft with the uploaded video URL
      setDirectionDraft(prev => ({
        ...prev,
        element: 'video',
        value: url
      }));

    } catch (err) {
      console.error('Error uploading video:', err);
      setVideoUploadError('Failed to upload video.');
    } finally {
      setVideoUploading(false);
    }
  };

  // Fetch user's uploads
  const fetchUserUploads = async (type) => {
    if (!userData || !userData.account_id) {
      console.error('User data not available');
      return;
    }

    setUploadsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/uploads?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserUploads(response.data.uploads);
      setShowUploadsModal(true);
    } catch (err) {
      console.error('Error fetching uploads:', err);
    } finally {
      setUploadsLoading(false);
    }
  };

  // Move initialDirection up
  const moveDirectionUp = (idx) => {
    if (idx === 0) return;
    setAssignment(prev => {
      const arr = [...prev.initialdirections];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return { ...prev, initialdirections: arr };
    });
  };
  // Move initialDirection down
  const moveDirectionDown = (idx) => {
    if (idx === assignment.initialdirections.length - 1) return;
    setAssignment(prev => {
      const arr = [...prev.initialdirections];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return { ...prev, initialdirections: arr };
    });
  };

  // Move question up
  const moveQuestionUp = (idx) => {
    if (idx === 0) return;
    setAssignment(prev => {
      const arr = [...prev.questions];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return { ...prev, questions: arr };
    });
  };
  // Move question down
  const moveQuestionDown = (idx) => {
    if (idx === assignment.questions.length - 1) return;
    setAssignment(prev => {
      const arr = [...prev.questions];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return { ...prev, questions: arr };
    });
  };

  const handleRemoveChoice = (idx) => {
    setQuestionDraft(q => {
      const newChoices = q.choices.filter((_, i) => i !== idx);
      // If the removed choice was the answer, clear answer
      let newAnswer = q.answer;
      if (q.type === 'singleChoice' && q.choices[idx] === q.answer) {
        newAnswer = '';
      } else if (q.type === 'multipleChoice' && Array.isArray(q.answer)) {
        // Remove the choice from the answer array for multiple choice
        newAnswer = q.answer.filter(ans => ans !== q.choices[idx]);
      }
      return { ...q, choices: newChoices, answer: newAnswer };
    });
    // If editing this choice, exit edit mode
    if (choiceEditIndex === idx) {
      setChoiceEditIndex(null);
      setChoiceEditValue('');
    }
  };

  const handleUpdateChoice = (idx) => {
    setQuestionDraft(q => {
      const newChoices = [...q.choices];
      const oldChoice = newChoices[idx];
      newChoices[idx] = choiceEditValue;
      // If the answer was the old value, update to new value
      let newAnswer = q.answer;
      if (q.type === 'singleChoice' && q.answer === oldChoice) {
        newAnswer = choiceEditValue;
      } else if (q.type === 'multipleChoice' && Array.isArray(q.answer)) {
        // Update the choice in the answer array for multiple choice
        newAnswer = q.answer.map(ans => ans === oldChoice ? choiceEditValue : ans);
      }
      return { ...q, choices: newChoices, answer: newAnswer };
    });
    setChoiceEditIndex(null);
    setChoiceEditValue('');
  };

  const canSubmitDirectionDraft = useMemo(() => {
    if (directionDraft.element === 'richText') return Boolean(directionDraft.value && directionDraft.value.trim());
    if (directionDraft.element === 'image') return Boolean(directionDraft.imageUrl && directionDraft.imageWidth && directionDraft.imageHeight);
    if (directionDraft.element === 'video') return Boolean(directionDraft.value && directionDraft.value.trim());
    return false;
  }, [directionDraft]);

  // Create or update assignment
  const createAssignment = async () => {
    try {
      // Validate that critical data is loaded
      if (!concentrationsLoaded || pathwayRoots.length === 0 || criticalDataError) {
        alert('Critical data (pathways / subjects) is not loaded properly. Please refresh the page and try again.');
        return;
      }
      
      // In edit mode, also check if assignment data is loaded
      if (isEditMode && !assignmentLoaded) {
        alert('Assignment data is not loaded. Please refresh the page and try again.');
        return;
      }
      
      // Validate required fields
      if (!assignment.title.trim()) {
        alert('Assignment title is required');
        return;
      }
      if (!assignment.concentration) {
        alert('Primary pathway (CTE concentration or course subject) is required');
        return;
      }
      if (assignment.hours == null || assignment.hours < 0) {
        alert('Hours is required and must be 0 or greater');
        return;
      }
      if (assignment.gradingresulttype === 'Points' && (assignment.maxpoints == null || assignment.maxpoints < 0)) {
        alert('Max Points is required and must be 0 or greater');
        return;
      }

      setSubmitting(true);
      setSubmissionStatus('submitting');

      // Get the auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        alert('Please log in to save an assignment');
        setSubmitting(false);
        setSubmissionStatus('idle');
        return;
      }

      // Extract concentrations from selected concentrations (excluding primary concentration)
      const otherConcentrationsFromSelected = selectedConcentrations.filter(
        concentrationId => concentrationId !== assignment.concentration
      );

      // Prepare the assignment data
      const assignmentData = {
        title: assignment.title,
        gradingresulttype: assignment.gradingresulttype,
        initialdirections: assignment.initialdirections,
        questions: assignment.questions,
        hours: assignment.hours,
        maxpoints: assignment.maxpoints,
        concentration: assignment.concentration,
        otherconcentrations: otherConcentrationsFromSelected,
        primary_taxonomy_id: assignment.concentration || null,
        additional_taxonomy_ids: otherConcentrationsFromSelected,
        taxonomy: assignment.taxonomy,
        createdby: userData.account_id
      };

      // Debug: Log the assignment data being sent
      console.log('Assignment data being sent to server:', assignmentData);
      console.log('Full assignment object:', assignment);
      console.log('User data:', userData);
      console.log('User account_id:', userData?.account_id);
      console.log('Is edit mode:', isEditMode);
      console.log('Assignment ID:', assignmentId);

      // Make the API call
      const url = isEditMode ? `/api/assignments/${assignmentId}` : '/api/assignments';
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(assignmentData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error response:', errorData);
        console.error('Response status:', response.status);
        console.error('Response status text:', response.statusText);
        throw new Error(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} assignment`);
      }

      const result = await response.json();
      setLastSubmissionResult(result);
      setSubmissionStatus('success');
      
      if (!isEditMode) {
        // Reset the form after successful creation (only for new assignments)
        setAssignment(initialAssignment);
        setDirectionDraft({ 
          element: 'richText', 
          value: '',
          imageUrl: '',
          imageWidth: '',
          imageHeight: ''
        });
        setQuestionDraft({ 
          type: 'directions', 
          gradingType: 'Points', 
          label: '', 
          required: true, 
          choices: [], 
          answer: '', 
          maxPoints: 0, 
          responseType: '' 
        });
        setSelectedConcentrations([]);
        
        // Show success message with assignment ID for new assignments
        console.log('New assignment created with ID:', result.assignment.id);
      }
      
      // Hide overlay after showing success message
      if (isEditMode) {
        // In edit mode, show success message for 5 seconds then close
        setTimeout(() => {
          setSubmitting(false);
          setSubmissionStatus('idle');
        }, 5000);
      }
      // In create mode, don't auto-hide the popup - let user click the button
      
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} assignment:`, error);
      setSubmissionStatus('error');
      setTimeout(() => {
        setSubmitting(false);
        setSubmissionStatus('idle');
      }, 1800);
    }
  };

  // Render — nothing interactive until all initial data is ready; errors use full gate overlay
  const mainReady =
    !submissionsRestrictionActive &&
    !loading &&
    !criticalDataError &&
    !error;
  const showSectionNav = mainReady;
  const showLoadOverlay = loading;
  const showDataErrorOverlay = !loading && (criticalDataError || error);
  const dataErrorText = criticalDataError || error || '';
  const isCriticalDataError = Boolean(criticalDataError);

  return (
    <div
      className={`assignment-builder${showSectionNav ? ' assignment-builder--with-section-nav' : ''}`}
    >
      {/* Block entire viewport while fetching pathways / taxonomy / (edit) assignment — no underlay to click through */}
      {showLoadOverlay && (
        <div
          className="ab-page-gate ab-page-gate--loading"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="ab-page-gate__panel">
            <div className="ab-page-gate__spinner" aria-hidden />
            <div className="ab-page-gate__title">
              Loading {isEditMode ? 'Assignment Editor' : 'Assignment Builder'}…
            </div>
            <div className="ab-page-gate__subtitle">Please wait while we load the required data.</div>
          </div>
        </div>
      )}

      {showDataErrorOverlay && (
        <div
          className="ab-page-gate ab-page-gate--error"
          role="alert"
        >
          <div className="ab-page-gate__panel ab-page-gate__panel--error">
            <div className="ab-page-gate__error-icon" aria-hidden>⚠️</div>
            <h2 className="ab-page-gate__error-heading">
              {isCriticalDataError
                ? 'Couldn’t load required data'
                : isEditMode
                  ? 'Couldn’t load this assignment'
                  : 'Couldn’t open Assignment Builder'}
            </h2>
            <p className="ab-page-gate__error-text">{dataErrorText}</p>
            <p className="ab-page-gate__error-hint">Reload the page to try again. If the problem continues, contact your administrator.</p>
            <button
              type="button"
              className="ab-page-gate__reload-btn"
              onClick={() => window.location.reload()}
            >
              Reload page
            </button>
          </div>
        </div>
      )}

      {/* Critical Error Container for Submissions */}
      {isEditMode && submissionsRestrictionActive && (
        <div className="ab-page-gate ab-page-gate--submissions" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
        }}>
          <div style={{
            background: 'white',
            padding: '40px',
            borderRadius: '12px',
            textAlign: 'center',
            maxWidth: '500px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⚠️</div>
            <h2 style={{ color: '#dc2626', marginBottom: '16px' }}>Critical Error</h2>
            <p style={{ color: '#374151', marginBottom: '24px', fontSize: '16px', lineHeight: '1.5' }}>
              This assignment has existing submissions and cannot be edited at this time. The feature to edit assignments with submissions is currently in development.
            </p>
            <button 
              onClick={() => window.location.href = `/assignment?id=${assignmentId}&mode=previewWithAnswers`}
              style={{
                background: '#1D4E89',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              View Assignment in Preview Mode
            </button>
          </div>
        </div>
      )}
      
      {mainReady && (
        <>
        <div style={{ marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '4px' }}>
              <h1 style={{ margin: 0 }}>{isEditMode ? 'Edit Assignment' : 'Assignment Builder'}</h1>
              {isEditMode && (
                <button
                  onClick={() => window.open(`/assignment?id=${assignmentId}&mode=previewWithAnswers`, '_blank')}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#1D4E89',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'background-color 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#153a6b';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#1D4E89';
                  }}
                >
                  Preview current version
                </button>
              )}
            </div>
            {isEditMode && (
              <div style={{ 
                fontSize: '14px', 
                color: '#666',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>Assignment ID: {assignmentId}</span>
                <span>•</span>
                <span>Edit Mode</span>
              </div>
            )}
          </div>
        </div>
        <div className="ab-main-below-question-dock">
          <div className="ab-section-nav" aria-label="Jump to section">
            {abSectionNavItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className="ab-section-nav-btn"
                title={item.id === 'ab-section-preview' ? 'Open preview popup' : item.label}
                onClick={() => {
                  if (item.id === 'ab-section-preview') {
                    openPreviewModal('user');
                    return;
                  }
                  scrollToAbSection(item.id);
                }}
              >
                <span aria-hidden>{item.icon}</span>
                <span className="ab-section-nav-label">{item.label}</span>
              </button>
            ))}
          </div>
          {/* Edit Mode Info Banner */}
          {isEditMode && assignmentLoaded && !submissionsRestrictionActive && (
            <div style={{
              padding: '16px',
              backgroundColor: '#e3f2fd',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #bbdefb',
              color: '#1565c0'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                📝 Editing Assignment: {assignment.title}
              </div>
              <div style={{ fontSize: '14px' }}>
                You are currently editing an existing assignment. Use the <strong>Update</strong> button (bottom-right) to save changes.
              </div>
            </div>
          )}
          
          <div id="ab-section-setup" className="ab-section ab-section--setup ab-section-anchor">
            <div className="ab-section__head">
              <h2 className="ab-section__title">Setup</h2>
              <p className="ab-section__hint">
                Title, type, hours, max points (when applicable), and primary pathway.
              </p>
            </div>
            <div className="ab-setup-grid">
              <div className="ab-setup-field ab-setup-field--title">
                <label className="ab-setup-label">
                  Assignment title <span className="ab-setup-req">*</span>
                </label>
                <input 
                  className="form-control"
                  value={assignment.title} 
                  onChange={e => setAssignment(a => ({ ...a, title: e.target.value }))} 
                  placeholder="Title…"
                />
              </div>
              <div className="ab-setup-field ab-setup-field--type">
                <label className="ab-setup-label">
                  Type <span className="ab-setup-req">*</span>
                </label>
                <select className="form-control" value={assignment.gradingresulttype} onChange={handleTypeChange}>
                  <option value="Points">Points</option>
                  <option value="Survey">Survey</option>
                </select>
              </div>
              <div className="ab-setup-field ab-setup-field--hours">
                <label className="ab-setup-label">
                  Hours <span className="ab-setup-req">*</span>
                </label>
                <input 
                  type="number" 
                  min={0} 
                  step={0.1} 
                  value={assignment.hours} 
                  onChange={e => setAssignment(a => ({ ...a, hours: Number(e.target.value) }))} 
                  className="form-control"
                />
              </div>
              <div className="ab-setup-field ab-setup-field--maxpoints">
                <label className="ab-setup-label">
                  Max pts <span className="ab-setup-req">*</span>
                </label>
                <input 
                  type="number" 
                  min={0} 
                  disabled 
                  value={assignment.maxpoints ?? 0} 
                  className="form-control"
                />
                <div
                  className="ab-setup-field-hint"
                  title={
                    assignment.gradingresulttype === 'Points'
                      ? 'Auto-calculated from question point totals'
                      : 'Max points do not apply for Survey assignments'
                  }
                >
                  {assignment.gradingresulttype === 'Points' ? 'Auto from questions' : 'N/A for Survey'}
                </div>
              </div>
              <div className="ab-setup-field ab-setup-field--pathway">
                <div className="ab-setup-pathway-row">
                  <label className="ab-setup-label ab-setup-label--inline">
                    Primary pathway <span className="ab-setup-req">*</span>
                  </label>
                  <PathwayScopeToggle
                    value={primaryScopeFilter}
                    onChange={handlePrimaryScopeChange}
                    hasConcentrations={hasCtePathways}
                    hasSubjects={hasSubjectPathways}
                    idPrefix="ab-primary-scope"
                  />
                </div>
                <select 
                  className="form-control"
                  value={assignment.concentration || ''} 
                  onChange={e => setAssignment(a => ({ ...a, concentration: e.target.value }))}
                  required
                >
                  <option value="">
                    {primaryScopeFilter === 'concentration'
                      ? 'Select a CTE pathway'
                      : 'Select a course subject'}
                  </option>
                  {primaryOptionsForScope.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          
          {/* Taxonomy Selection */}
          <div id="ab-section-taxonomy" className="ab-section ab-section--taxonomy ab-section-anchor">
            <div className="ab-section__head">
              <h2 className="ab-section__title">Taxonomy</h2>
              <p className="ab-section__hint">
                Select additional pathways if needed, then attach skills, standards, and objectives under each pathway or subject.
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 10,
                marginBottom: 10,
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748b' }}>
                Pathway scope
              </span>
              <PathwayScopeToggle
                value={taxonomyScopeFilter}
                onChange={setTaxonomyScopeFilter}
                hasConcentrations={hasCtePathways}
                hasSubjects={hasSubjectPathways}
                idPrefix="ab-taxonomy-scope"
              />
            </div>
            {(assignmentLoaded || !isEditMode) && (
              <TaxonomySelector
                key={`taxonomy-${assignmentId || 'new'}-${selectedConcentrations.length}-${taxonomyScopeFilter}`}
                selectedTaxonomy={assignment.taxonomy}
                onTaxonomyChange={(taxonomy) => setAssignment(a => ({ ...a, taxonomy }))}
                concentrations={taxonomyPathwayOptions}
                disabled={submitting || submissionsRestrictionActive}
                primaryConcentration={assignment.concentration}
                selectedConcentrations={selectedConcentrations}
                onSelectedConcentrationsChange={setSelectedConcentrations}
                isStudentRole={false}
                taxonomyItems={taxonomyItems}
              />
            )}
          </div>
          
          <div id="ab-section-directions" className="ab-section ab-section--directions ab-section-anchor">
            <div className="ab-section__head">
              <div className="ab-section__head-row ab-section__head-row--directions">
                <h2 className="ab-section__title">Initial directions</h2>
                <div className="ab-direction-add-wrap">
                  <span className="ab-direction-add-label" id="ab-add-direction-hint">
                    Add direction
                  </span>
                  <button
                    type="button"
                    className="ab-add-round-btn ab-add-round-btn--direction"
                    onClick={openDirectionModalForAdd}
                    title="Add new direction"
                    aria-label="Add new direction"
                    aria-describedby="ab-add-direction-hint"
                  >
                    <span aria-hidden>+</span>
                  </button>
                </div>
              </div>
              <p className="ab-section__hint">
                Shown at the top of the assignment, before questions. Add text, images, or video.
              </p>
              {assignment.initialdirections.length === 0 && (
                <p className="ab-section__rules">At least one direction element is required.</p>
              )}
            </div>
          {directionModalOpen && (
          <div
            className="ab-question-modal-backdrop"
            onClick={closeDirectionModal}
            role="presentation"
          >
            <div
              ref={directionEditorRef}
              className="ab-question-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ab-direction-modal-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="ab-question-modal__inner">
              <div className="ab-question-modal__head">
                <button
                  type="button"
                  className="ab-question-modal__close"
                  onClick={closeDirectionModal}
                  aria-label="Close direction editor"
                >
                  ×
                </button>
                <h3 className="ab-question-modal__title" id="ab-direction-modal-title">
                  {editingDirectionId ? 'Update direction' : 'Add new direction'}
                </h3>
                <div className="ab-question-modal__actions">
                  {editingDirectionId && (
                    <button
                      type="button"
                      className="ab-btn ab-btn--ghost ab-question-modal__action"
                      onClick={closeDirectionModal}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    className="ab-btn ab-btn--primary ab-question-modal__action ab-question-modal__action--primary"
                    onClick={addOrUpdateDirection}
                    disabled={!canSubmitDirectionDraft}
                  >
                    {editingDirectionId ? 'Update direction' : 'Add direction'}
                  </button>
                </div>
              </div>
              <div className="ab-question-modal__body">
          {!editingDirectionId && (
            <p className="ab-section__append-hint" style={{ margin: '2px 0 10px' }}>
              New directions are appended to the end of the current directions list.
            </p>
          )}
          <div className="editor-section ab-editor-panel">
            <div className="form-group">
              <p className="ab-direction-type-title" id="ab-direction-type-label">
                Direction Type
              </p>
              <div
                className="ab-direction-type-tags"
                role="radiogroup"
                aria-labelledby="ab-direction-type-label"
              >
                {directionTypes.map((dt) => (
                  <label
                    key={dt.value}
                    className="ab-direction-type-tag"
                  >
                    <input
                      type="radio"
                      name="ab-direction-element"
                      value={dt.value}
                      checked={directionDraft.element === dt.value}
                      onChange={() =>
                        setDirectionDraft((d) => ({
                          ...d,
                          element: dt.value,
                          value: '',
                          imageUrl: '',
                          imageWidth: '',
                          imageHeight: ''
                        }))
                      }
                    />
                    <span className="ab-direction-type-tag__text">{dt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Upload functionality for images and videos */}
            {(directionDraft.element === 'image' || directionDraft.element === 'video') && (
              <div style={{ marginTop: 16 }}>
                <div style={{ 
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '16px',
                  border: '1px solid #e9ecef',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                    <button
                      type="button"
                      onClick={() => fetchUserUploads(directionDraft.element === 'image' ? 'image' : 'video')}
                      disabled={uploadsLoading}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#5a6268'}
                      onMouseLeave={(e) => e.target.style.background = '#6c757d'}
                    >
                      <span style={{ fontSize: '14px' }}>🔍</span>
                      {uploadsLoading ? 'Loading...' : `Find ${directionDraft.element === 'image' ? 'Images' : 'Videos'}`}
                    </button>
                  </div>
                  
                  <div style={{
                    border: '2px dashed #dee2e6',
                    borderRadius: '6px',
                    padding: '16px',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    background: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.borderColor = '#adb5bd'}
                  onMouseLeave={(e) => e.target.style.borderColor = '#dee2e6'}
                  >
                    <input
                      type="file"
                      accept={directionDraft.element === 'image' ? 'image/*' : 'video/*'}
                      onChange={directionDraft.element === 'image' ? handleImageFileUpload : handleVideoFileUpload}
                      disabled={directionDraft.element === 'image' ? imageUploading : videoUploading}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                      {directionDraft.element === 'image' ? '📸' : '🎥'}
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#495057' }}>
                      {directionDraft.element === 'image' 
                        ? (imageUploading ? 'Uploading...' : 'Click to upload or drag image here')
                        : (videoUploading ? 'Uploading...' : 'Click to upload or drag video here')
                      }
                    </div>
                  </div>
                  
                  {(directionDraft.element === 'image' ? imageUploading : videoUploading) && (
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      marginTop: '12px',
                      padding: '8px',
                      background: '#e9ecef',
                      borderRadius: '6px'
                    }}>
                      <div style={{ 
                        width: '16px', 
                        height: '16px', 
                        border: '2px solid #adb5bd', 
                        borderTop: '2px solid #495057', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite' 
                      }}></div>
                      <span style={{ fontSize: '12px', color: '#495057' }}>
                        Uploading {directionDraft.element === 'image' ? 'image' : 'video'}...
                      </span>
                    </div>
                  )}
                  
                  {(directionDraft.element === 'image' ? imageUploadError : videoUploadError) && (
                    <div style={{ 
                      color: '#721c24', 
                      fontSize: '12px', 
                      marginTop: '8px',
                      padding: '8px',
                      background: '#f8d7da',
                      borderRadius: '6px',
                      border: '1px solid #f5c6cb'
                    }}>
                      ⚠️ {directionDraft.element === 'image' ? imageUploadError : videoUploadError}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {directionDraft.element === 'richText' && (
              <div style={{ marginTop: 16 }}>
                <label>Content</label>
                <ReactQuill 
                  theme="snow" 
                  value={directionDraft.value} 
                  onChange={val => setDirectionDraft(d => ({ ...d, value: val }))} 
                />
              </div>
            )}
            
            {directionDraft.element === 'image' && (
              <div style={{ marginTop: 16 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Image URL</label>
                    <input 
                      className="form-control"
                      value={directionDraft.imageUrl} 
                      onChange={e => setDirectionDraft(d => ({ ...d, imageUrl: e.target.value }))} 
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Width (px)</label>
                    <input 
                      type="number" 
                      min={1} 
                      className="form-control"
                      value={directionDraft.imageWidth} 
                      onChange={e => setDirectionDraft(d => ({ ...d, imageWidth: e.target.value }))} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Height (px)</label>
                    <input 
                      type="number" 
                      min={1} 
                      className="form-control"
                      value={directionDraft.imageHeight} 
                      onChange={e => setDirectionDraft(d => ({ ...d, imageHeight: e.target.value }))} 
                    />
                  </div>
                </div>
              </div>
            )}
            
            {directionDraft.element === 'video' && (
              <div style={{ marginTop: 16 }}>
                <div className="form-group">
                  <label>Video URL</label>
                  <input 
                    className="form-control"
                    value={directionDraft.value} 
                    onChange={e => setDirectionDraft(d => ({ ...d, value: e.target.value }))} 
                    placeholder="enter video url"
                  />
                </div>
              </div>
            )}
          </div>
          </div>
          </div>
          </div>
          </div>
          )}
          <div className="directions-list ab-block-list">
            {assignment.initialdirections.map((d, idx) => (
              <div key={d._id} className="card fade-in">
                <div className="card-header">
                  <div className="card-content">
                    {d.element === 'richText' && <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: d.value }} />}
                    {d.element === 'image' && renderImageFromTag(d.value)}
                    {d.element === 'video' && renderVideoFromUrl(d.value)}
                  </div>
                  <div className="card-actions card-actions--compact">
                    <button 
                      type="button"
                      className="icon-button ab-icon-tight" 
                      onClick={() => editDirection(d)} 
                      title="Edit"
                    >
                      <PencilIcon />
                    </button>
                    <button 
                      type="button"
                      className="icon-button danger ab-icon-tight" 
                      onDoubleClick={() => removeDirection(d._id)} 
                      title="Double-click to remove"
                    >
                      <TrashIcon />
                    </button>
                    <div className="ab-order-pair" role="group" aria-label="Reorder">
                      <button 
                        type="button"
                        className="ab-order-btn" 
                        onClick={() => moveDirectionUp(idx)} 
                        disabled={idx === 0} 
                        title="Move up"
                      >
                        <ChevronUpIcon />
                      </button>
                      <button 
                        type="button"
                        className="ab-order-btn" 
                        onClick={() => moveDirectionDown(idx)} 
                        disabled={idx === assignment.initialdirections.length - 1} 
                        title="Move down"
                      >
                        <ChevronDownIcon />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </div>

          <div id="ab-section-questions" className="ab-section ab-section--questions ab-section-anchor">
            <div className="ab-section__head">
              <div className="ab-section__head-row ab-section__head-row--questions">
                <h2 className="ab-section__title">Questions</h2>
                <div className="ab-question-add-wrap">
                  <span className="ab-question-add-label" id="ab-add-question-hint">
                    Add a question
                  </span>
                  <button
                    type="button"
                    className="ab-add-round-btn ab-add-round-btn--question"
                    onClick={openQuestionDockForAdd}
                    title="Add new question"
                    aria-label="Add new question"
                    aria-describedby="ab-add-question-hint"
                  >
                    <span aria-hidden>+</span>
                  </button>
                </div>
              </div>
              <p className="ab-section__hint">
                Build and order the question list; students answer in sequence.
              </p>
              {!(assignment.questions.length > 0 &&
                (assignment.gradingresulttype !== 'Points' || assignment.questions.some(q => q.maxPoints > 0))) && (
                <p className="ab-section__rules">
                  {assignment.gradingresulttype === 'Points'
                    ? 'Add at least one question, and ensure at least one question has more than 0 points.'
                    : 'Add at least one question.'}
                </p>
              )}
            </div>
          <div className="questions-list ab-block-list">
            {assignment.questions.map((q, idx) => {
              const scAny = q.type === 'singleChoice' && (!q.answer || !q.choices || !q.choices.includes(q.answer));
              const mcAny = q.type === 'multipleChoice' && (!Array.isArray(q.answer) || q.answer.length === 0);
              const showManualTag = assignment.gradingresulttype === 'Points' && (q.type === 'openAnswer' || q.type === 'upload');
              const hasActionHints =
                showManualTag ||
                (q.type === 'singleChoice' && scAny) ||
                q.type === 'multipleChoice';
              return (
              <div key={`${q._id}-${q.type}`} className="card fade-in">
                <div className="card-header">
                  <div className="card-content">
                    <div className="question-preview">
                      <span className="question-number">{idx + 1}</span>
                      
                      {/* Preview for question types */}
                      {q.type === 'directions' && <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} />}
                      
                      {q.type === 'video' && (
                        <div>
                          <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 12 }} />
                          {renderVideoFromUrl(q.answer)}
                        </div>
                      )}
                      
                      {q.type === 'upload' && (
                        <div>
                          <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 12 }} />
                          <input type="file" disabled className="form-control" style={{ maxWidth: 300 }} />
                        </div>
                      )}
                      
                      {q.type === 'singleChoice' && (
                        <div>
                          <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 12 }} />
                          <ul className="choice-list ab-choice-list--badges">
                            {(q.choices || []).map((c, i) => (
                              <li key={i} className="choice-item">
                                <div className="ab-choice-left">
                                  <input type="radio" disabled checked={q.answer === c} style={{ marginRight: 12 }} />
                                  <span>{c}</span>
                                </div>
                                {!scAny && q.answer === c && (
                                  <span className="ab-answer-badge ab-answer-badge--correct">Correct</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {q.type === 'multipleChoice' && (
                        <div>
                          <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 12 }} />
                          <ul className="choice-list ab-choice-list--badges">
                            {(q.choices || []).map((c, i) => (
                              <li key={i} className="choice-item">
                                <div className="ab-choice-left">
                                  <input type="checkbox" disabled checked={Array.isArray(q.answer) && q.answer.includes(c)} style={{ marginRight: 12 }} />
                                  <span>{c}</span>
                                </div>
                                {!mcAny && Array.isArray(q.answer) && q.answer.includes(c) && (
                                  <span className="ab-answer-badge ab-answer-badge--mc" title="Must select all options marked this way (all or nothing)">Correct*</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {q.type === 'openAnswer' && (
                        <div>
                          <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 12 }} />
                          {q.responseType === 'Number-Float' ? (
                            <input type="number" step="any" disabled className="form-control" style={{ maxWidth: 200 }} />
                          ) : (
                            <textarea rows={3} disabled className="form-control" style={{ maxWidth: 400 }} />
                          )}
                        </div>
                      )}
                      
                      {q.type !== 'directions' && q.type !== 'video' && q.type !== 'upload' && q.type !== 'singleChoice' && q.type !== 'multipleChoice' && q.type !== 'openAnswer' && <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} />}
                    </div>
                  </div>
                  <div className="card-actions card-actions--compact ab-card-actions-bar">
                    {hasActionHints && (
                      <div className="ab-card-actions-hints">
                        {showManualTag && (
                          <span className="ab-manual-grading-tag ab-manual-grading-tag--in-actions" role="status">
                            Manual grading required
                          </span>
                        )}
                        {q.type === 'singleChoice' && scAny && (
                          <div className="ab-answer-hint--any ab-answer-hint--in-actions" role="status">
                            Any choice is accepted
                          </div>
                        )}
                        {q.type === 'multipleChoice' && mcAny && (
                          <div className="ab-answer-hint--any ab-answer-hint--in-actions" role="status">
                            Any selection is accepted
                          </div>
                        )}
                        {q.type === 'multipleChoice' && !mcAny && (
                          <p className="ab-answer-legend ab-answer-legend--in-actions">
                            Options marked <span className="ab-answer-badge ab-answer-badge--mc" style={{ verticalAlign: 'middle' }}>Correct*</span> must
                            all be selected for a correct answer. Partial credit is not given (all or nothing).
                          </p>
                        )}
                      </div>
                    )}
                    <div className="ab-card-actions-trail">
                      {q.maxPoints != null && q.type !== 'video' && q.type !== 'directions' && (
                        <span className="ab-card-pts">
                          {q.maxPoints} pts
                        </span>
                      )}
                    <button 
                      type="button"
                      className="icon-button ab-icon-tight" 
                      onClick={() => editQuestion(q)} 
                      title="Edit"
                    >
                      <PencilIcon />
                    </button>
                    <button 
                      type="button"
                      className="icon-button danger ab-icon-tight" 
                      onDoubleClick={() => removeQuestion(q._id)} 
                      title="Double-click to remove"
                    >
                      <TrashIcon />
                    </button>
                    <div className="ab-order-pair" role="group" aria-label="Reorder">
                      <button 
                        type="button"
                        className="ab-order-btn" 
                        onClick={() => moveQuestionUp(idx)} 
                        disabled={idx === 0} 
                        title="Move up"
                      >
                        <ChevronUpIcon />
                      </button>
                      <button 
                        type="button"
                        className="ab-order-btn" 
                        onClick={() => moveQuestionDown(idx)} 
                        disabled={idx === assignment.questions.length - 1} 
                        title="Move down"
                      >
                        <ChevronDownIcon />
                      </button>
                    </div>
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
          </div>
          {questionDockOpen && (
          <div
            className="ab-question-modal-backdrop"
            onClick={closeQuestionDock}
            role="presentation"
          >
            <div
              ref={questionEditorRef}
              className="ab-question-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ab-question-modal-title"
              onClick={e => e.stopPropagation()}
            >
              <div className="ab-question-modal__inner">
              <div className="ab-question-modal__head">
                <button
                  type="button"
                  className="ab-question-modal__close"
                  onClick={closeQuestionDock}
                  aria-label="Close question editor"
                >
                  ×
                </button>
                <h3 className="ab-question-modal__title" id="ab-question-modal-title">
                  {editingQuestionId ? 'Update question' : 'Add new question'}
                </h3>
                <div className="ab-question-modal__actions">
                  {editingQuestionId && (
                    <button
                      type="button"
                      className="ab-btn ab-btn--ghost ab-question-modal__action"
                      onClick={closeQuestionDock}
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    className="ab-btn ab-btn--primary ab-question-modal__action ab-question-modal__action--primary"
                    onClick={addOrUpdateQuestion}
                    disabled={
                      !labelHasText(questionDraft.label) ||
                      ((questionDraft.type === 'singleChoice' || questionDraft.type === 'multipleChoice') && (!questionDraft.choices || questionDraft.choices.length === 0))
                    }
                  >
                    {editingQuestionId ? 'Update question' : 'Add question'}
                  </button>
                </div>
              </div>
              <div className="ab-question-modal__body">
                {!editingQuestionId && (
                  <p className="ab-section__append-hint" style={{ margin: '2px 0 10px' }}>
                    New questions are appended to the end of the current question list.
                  </p>
                )}
                <div className="editor-section ab-editor-panel">
            <div className="form-row">
              <div className="form-group">
                <label>Question Type</label>
                <select 
                  className="form-control"
                  value={questionDraft.type} 
                  onChange={e => setQuestionDraft(q => ({ ...q, type: e.target.value, choices: [], answer: '', responseType: '' }))}
                >
                  {questionTypes.map(qt => <option key={qt.value} value={qt.value}>{qt.label}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Grading Type</label>
                <select 
                  className="form-control"
                  value={questionDraft.gradingType} 
                  disabled 
                >
                  {gradingTypes.map(gt => <option key={gt.value} value={gt.value}>{gt.label}</option>)}
                </select>
                <small style={{ 
                  color: '#666', 
                  fontSize: '0.875rem', 
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px'
                }}>
                  Set by assignment type: {assignment.gradingresulttype}
                </small>
              </div>
            </div>
            
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Question Label</label>
              <ReactQuill 
                theme="snow" 
                value={questionDraft.label} 
                onChange={val => setQuestionDraft(q => ({ ...q, label: val }))} 
              />
            </div>
            {['singleChoice', 'multipleChoice'].includes(questionDraft.type) && (
              <div style={{ marginTop: 24 }}>
                <label style={{ fontWeight: 600, color: '#4a5568', marginBottom: 12, display: 'block' }}>
                  Choices (check the box to set as correct answer)
                </label>
                <ul className="choice-list">
                  {(questionDraft.choices || []).map((c, i) => {
                    const scAny = questionDraft.type === 'singleChoice' && (!questionDraft.answer || !questionDraft.choices.includes(questionDraft.answer));
                    const scShowTag = questionDraft.type === 'singleChoice' && !scAny && questionDraft.answer === c;
                    const mcAny = questionDraft.type === 'multipleChoice' && (!Array.isArray(questionDraft.answer) || questionDraft.answer.length === 0);
                    const mcShowTag = questionDraft.type === 'multipleChoice' && !mcAny && Array.isArray(questionDraft.answer) && questionDraft.answer.includes(c);
                    return (
                    <li
                      key={i}
                      className="choice-item"
                      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, flexWrap: 'wrap', gap: 6 }}>
                        <span className="choice-number">{i + 1}.</span>
                        
                        {/* Edit/Remove icons for singleChoice and multipleChoice */}
                        {(questionDraft.type === 'singleChoice' || questionDraft.type === 'multipleChoice') && (
                          <>
                            <button
                              onClick={() => { setChoiceEditIndex(i); setChoiceEditValue(c); }}
                              title="Edit Choice"
                              className="icon-button"
                            >
                              <PencilIcon />
                            </button>
                            <button
                              onDoubleClick={() => handleRemoveChoice(i)}
                              title="Double-click to remove choice"
                              className="icon-button danger"
                            >
                              <TrashIcon />
                            </button>
                            
                            {/* Checkbox for answer(s) */}
                            {questionDraft.type === 'singleChoice' && (
                              <input
                                type="checkbox"
                                checked={questionDraft.answer === c}
                                onChange={e => {
                                  setQuestionDraft(q => ({
                                    ...q,
                                    answer: e.target.checked ? c : ''
                                  }));
                                }}
                                style={{ marginRight: 12 }}
                              />
                            )}
                            {questionDraft.type === 'multipleChoice' && (
                              <input
                                type="checkbox"
                                checked={Array.isArray(questionDraft.answer) && questionDraft.answer.includes(c)}
                                onChange={e => {
                                  setQuestionDraft(q => {
                                    let newAnswer = Array.isArray(q.answer) ? [...q.answer] : [];
                                    if (e.target.checked) {
                                      newAnswer.push(c);
                                    } else {
                                      newAnswer = newAnswer.filter(ans => ans !== c);
                                    }
                                    return { ...q, answer: newAnswer };
                                  });
                                }}
                                style={{ marginRight: 12 }}
                              />
                            )}
                          </>
                        )}
                        
                        {/* Inline edit for choice */}
                        {choiceEditIndex === i ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                            <input
                              className="form-control"
                              value={choiceEditValue}
                              onChange={e => setChoiceEditValue(e.target.value)}
                              style={{ flex: 1 }}
                              autoFocus
                              onBlur={() => handleUpdateChoice(i)}
                              onKeyDown={e => { if (e.key === 'Enter') handleUpdateChoice(i); }}
                            />
                            <button type="button" className="ab-btn ab-btn--primary ab-btn--compact" onClick={() => handleUpdateChoice(i)}>OK</button>
                          </div>
                        ) : (
                          <span style={{ flex: 1, minWidth: 120 }}>{c}</span>
                        )}
                      </div>
                      {scShowTag && (
                        <span className="ab-answer-badge ab-answer-badge--correct">Correct</span>
                      )}
                      {mcShowTag && (
                        <span className="ab-answer-badge ab-answer-badge--mc" title="Must select all options marked this way (all or nothing)">Correct*</span>
                      )}
                    </li>
                    );
                  })}
                </ul>
                
                <div className="ab-choice-add-row">
                  <input 
                    className="form-control"
                    value={choiceDraft} 
                    onChange={e => setChoiceDraft(e.target.value)} 
                    placeholder="Enter choice text..."
                    onKeyDown={e => { if (e.key === 'Enter') addChoice(); }}
                  />
                  <button 
                    type="button"
                    className="ab-btn ab-btn--muted"
                    onClick={addChoice} 
                    disabled={!choiceDraft.trim()}
                  >
                    Add choice
                  </button>
                </div>
                
                {/* Answer info */}
                {questionDraft.type === 'singleChoice' && (!questionDraft.answer || !questionDraft.choices.includes(questionDraft.answer)) && (
                  <div className="ab-answer-hint--any" style={{ marginTop: 12 }} role="status">
                    Any choice is accepted
                  </div>
                )}
                {questionDraft.type === 'multipleChoice' && (!Array.isArray(questionDraft.answer) || questionDraft.answer.length === 0) && (
                  <div className="ab-answer-hint--any" style={{ marginTop: 12 }} role="status">
                    Any selection is accepted
                  </div>
                )}
                {questionDraft.type === 'multipleChoice' && Array.isArray(questionDraft.answer) && questionDraft.answer.length > 0 && (
                  <p className="ab-answer-legend" style={{ marginTop: 12 }}>
                    Options marked <span className="ab-answer-badge ab-answer-badge--mc" style={{ verticalAlign: 'middle' }}>Correct*</span> must
                    all be selected for a correct answer. Partial credit is not given (all or nothing).
                  </p>
                )}
              </div>
            )}
            {questionDraft.type === 'openAnswer' && (
              <div style={{ marginTop: 24 }}>
                <div className="form-group">
                  <label>Response Type</label>
                  <select 
                    className="form-control"
                    value={questionDraft.responseType} 
                    onChange={e => setQuestionDraft(q => ({ ...q, responseType: e.target.value }))}
                  >
                    <option value="">Text</option>
                    <option value="Number-Float">Number (Float)</option>
                  </select>
                </div>
              </div>
            )}
            
            {questionDraft.type === 'video' && (
              <div style={{ marginTop: 24 }}>
                <div className="form-group">
                  <label>Video URL</label>
                  <input 
                    className="form-control"
                    value={questionDraft.answer} 
                    onChange={e => setQuestionDraft(q => ({ ...q, answer: e.target.value }))} 
                    placeholder="enter video url"
                  />
                </div>
                {questionDraft.answer && (
                  <div style={{ marginTop: 16 }}>
                    {renderVideoFromUrl(questionDraft.answer)}
                  </div>
                )}
              </div>
            )}
            
            {['singleChoice', 'multipleChoice', 'openAnswer', 'upload'].includes(questionDraft.type) && (
              <div
                style={{
                  marginTop: 24,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-end',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <div className="form-group" style={{ flex: '1 1 180px', minWidth: 0, marginBottom: 0 }}>
                  <label>Max Points</label>
                  <input 
                    type="number" 
                    min={0} 
                    className="form-control"
                    value={questionDraft.maxPoints} 
                    onChange={e => setQuestionDraft(q => ({ ...q, maxPoints: Number(e.target.value) }))} 
                    disabled={assignment.gradingresulttype !== 'Points'} 
                  />
                </div>
                {assignment.gradingresulttype === 'Points' && (questionDraft.type === 'openAnswer' || questionDraft.type === 'upload') && (
                  <span className="ab-manual-grading-tag ab-manual-grading-tag--in-actions" role="status" style={{ marginBottom: 2 }}>
                    Manual grading required
                  </span>
                )}
              </div>
            )}
            
            <div style={{ marginTop: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input 
                  type="checkbox" 
                  checked={true} 
                  disabled 
                  className="form-control"
                  style={{ width: 'auto', margin: 0 }}
                />
                <span style={{ fontSize: 14, color: '#666' }}>Required</span>
              </label>
            </div>
                </div>
              </div>
            </div>
            </div>
          </div>
          )}
          
          {/* Preview / JSON Modal */}
          {previewModalOpen && (
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1200,
                background: 'rgba(15, 23, 42, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16
              }}
              onClick={() => setPreviewModalOpen(false)}
            >
              <div
                style={{
                  width: 'min(1100px, 96vw)',
                  maxHeight: '90vh',
                  background: '#fff',
                  borderRadius: 14,
                  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.28)',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px 16px',
                    borderBottom: '1px solid #e5e7eb',
                    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'
                  }}
                >
                  <div
                    style={{
                      display: 'inline-flex',
                      gap: 6,
                      padding: 4,
                      borderRadius: 999,
                      background: '#e2e8f0',
                      border: '1px solid #cbd5e1'
                    }}
                  >
                    <button
                      type="button"
                      className="preview-tab"
                      onClick={() => setPreviewTab('user')}
                      style={{
                        border: 'none',
                        borderRadius: 999,
                        padding: '8px 14px',
                        fontWeight: 600,
                        fontSize: 13,
                        color: previewTab === 'user' ? '#fff' : '#334155',
                        background: previewTab === 'user' ? 'linear-gradient(135deg, #1D4E89 0%, #2563a8 100%)' : 'transparent',
                        boxShadow: previewTab === 'user' ? '0 4px 12px rgba(29, 78, 137, 0.28)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.18s ease'
                      }}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="preview-tab"
                      onClick={() => setPreviewTab('json')}
                      style={{
                        border: 'none',
                        borderRadius: 999,
                        padding: '8px 14px',
                        fontWeight: 600,
                        fontSize: 13,
                        color: previewTab === 'json' ? '#fff' : '#334155',
                        background: previewTab === 'json' ? 'linear-gradient(135deg, #1D4E89 0%, #2563a8 100%)' : 'transparent',
                        boxShadow: previewTab === 'json' ? '0 4px 12px rgba(29, 78, 137, 0.28)' : 'none',
                        cursor: 'pointer',
                        transition: 'all 0.18s ease'
                      }}
                    >
                      JSON
                    </button>
                  </div>
                  <button
                    type="button"
                    className="ab-btn ab-btn--ghost"
                    onClick={() => setPreviewModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
                <div style={{ overflowY: 'auto', padding: 16 }}>
                  {previewTab === 'json' && (
                    <div className="preview-content">
                      <h2>JSON</h2>
                      <pre>{JSON.stringify({
                        ...assignment,
                        otherconcentrations: selectedConcentrations.filter(id => id !== assignment.concentration)
                      }, null, 2)}</pre>
                    </div>
                  )}
                  {previewTab === 'user' && (
                    <div className="preview-content">
                      <h2>Preview</h2>
              <div 
                className="assignment-viewer-container"
                style={{
                  maxWidth: 900,
                  margin: '40px auto',
                  padding: 32,
                  background: '#fff',
                  borderRadius: 16,
                  boxShadow: '0 4px 24px 0 rgba(60,72,88,0.10), 0 1.5px 4px 0 rgba(60,72,88,0.08)',
                  border: '1px solid #f0f0f0',
                  position: 'relative'
                }}
              >
                <h2 className="assignment-title" style={{ marginTop: 0, marginBottom: 24, color: '#2d3748', fontSize: '28px', fontWeight: '700' }}>
                  {assignment.title || 'Untitled Assignment'}
                </h2>
                
                {/* Initial Directions Section */}
                {assignment.initialdirections && assignment.initialdirections.length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <h3 
                      style={{ 
                        margin: '0 0 16px 0', 
                        color: '#495057', 
                        fontSize: '20px', 
                        fontWeight: '600'
                      }}
                    >
                      Directions
                    </h3>
                    
                    <div style={{ 
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      {assignment.initialdirections.map((d, i) => (
                        <div key={d._id || i} style={{ marginBottom: 16 }}>
                          {d.element === 'richText' && (
                            <div 
                              className="directions-content rich-text-display"
                              dangerouslySetInnerHTML={{ __html: d.value }} 
                            />
                          )}
                          {d.element === 'image' && renderImageFromTag(d.value)}
                          {d.element === 'video' && renderVideoFromUrl(d.value)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Questions Section */}
                {assignment.questions && assignment.questions.length > 0 && (
                  <div style={{ 
                    marginBottom: 32,
                    padding: '20px',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    border: '1px solid transparent'
                  }}>
                    <h3 style={{ 
                      margin: '0 0 16px 0', 
                      color: '#495057', 
                      fontSize: '20px', 
                      fontWeight: '600'
                    }}>
                      Questions
                      {assignment.gradingresulttype === 'Points' && (
                        <span style={{ 
                          fontSize: '14px', 
                          color: '#28a745', 
                          fontWeight: '500',
                          marginLeft: '8px'
                        }}>
                          ({assignment.maxpoints || 0} points)
                        </span>
                      )}
                    </h3>
                    
                    <ol style={{ paddingLeft: 0, margin: 0 }}>
                      {assignment.questions.map((q, idx) => {
                        const scAnyM = q.type === 'singleChoice' && (!q.answer || !q.choices || !q.choices.includes(q.answer));
                        const mcAnyM = q.type === 'multipleChoice' && (!Array.isArray(q.answer) || q.answer.length === 0);
                        const showModalManual = assignment.gradingresulttype === 'Points' && (q.type === 'openAnswer' || q.type === 'upload');
                        const showModalScHint = q.type === 'singleChoice' && scAnyM;
                        const showModalMcAny = q.type === 'multipleChoice' && mcAnyM;
                        const showModalMcLegend = q.type === 'multipleChoice' && !mcAnyM;
                        const showModalPts = !isSurveyAssignment && typeof q.maxPoints === 'number' && q.maxPoints > 0;
                        const showModalMetaColumn = showModalManual || showModalScHint || showModalMcAny || showModalMcLegend || showModalPts;
                        return (
                        <li key={q._id || idx} className="question-item" style={{
                          listStyle: 'none',
                          marginBottom: 32,
                          background: '#fafbfc',
                          borderRadius: 12,
                          boxShadow: '0 2px 8px 0 rgba(60,72,88,0.07)',
                          padding: 24,
                          border: '1px solid #f0f0f0',
                          position: 'relative',
                        }}>
                          <span className="question-number" style={{ fontWeight: 700, fontSize: 18, marginRight: 12, color: '#1976d2', position: 'absolute', left: 16, top: 24 }}>
                            {idx + 1}.
                          </span>
                          
                          {/* Meta column: manual & SC/MC hints first, x pts last — top right, not in question body */}
                          {showModalMetaColumn && (
                            <div
                              className="ab-preview-modal-meta-col"
                              style={{
                                position: 'absolute',
                                top: 18,
                                right: 24,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 6,
                                zIndex: 1,
                                maxWidth: showModalMcLegend ? 'min(400px, 90%)' : 'min(220px, 42%)',
                                textAlign: 'right',
                              }}
                            >
                              {showModalManual && (
                                <span className="ab-manual-grading-tag ab-manual-grading-tag--in-actions" role="status">
                                  Manual grading required
                                </span>
                              )}
                              {showModalScHint && (
                                <div className="ab-answer-hint--any ab-answer-hint--in-actions" role="status" style={{ textAlign: 'right' }}>
                                  Any choice is accepted
                                </div>
                              )}
                              {showModalMcAny && (
                                <div className="ab-answer-hint--any ab-answer-hint--in-actions" role="status" style={{ textAlign: 'right' }}>
                                  Any selection is accepted
                                </div>
                              )}
                              {showModalMcLegend && (
                                <p className="ab-answer-legend ab-answer-legend--in-actions" style={{ textAlign: 'right' }}>
                                  Options marked <span className="ab-answer-badge ab-answer-badge--mc" style={{ verticalAlign: 'middle' }}>Correct*</span> must
                                  all be selected for a correct answer. Partial credit is not given (all or nothing).
                                </p>
                              )}
                              {showModalPts && (
                                <span className="points-display" style={{
                                  color: '#1976d2',
                                  fontWeight: 600,
                                  fontSize: 15,
                                  background: '#e3f0ff',
                                  borderRadius: 6,
                                  padding: '2px 10px',
                                }}>{q.maxPoints} pts</span>
                              )}
                            </div>
                          )}
                          
                          <div className="question-content" style={{ marginLeft: 32 }}>
                            {/* Directions/Video */}
                            {q.type === 'directions' && (
                              <div 
                                className="directions-content rich-text-display"
                                dangerouslySetInnerHTML={{ __html: q.label }} 
                              />
                            )}
                            
                            {q.type === 'video' && (
                              <div>
                                <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                                {renderVideoFromUrl(q.answer)}
                              </div>
                            )}
                            
                            {/* Single Choice */}
                            {q.type === 'singleChoice' && (
                              <div>
                                <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                                <ul className="choice-list" style={{ listStyle: 'none', paddingLeft: 0, margin: 0 }}>
                                  {(q.choices || []).map((c, i) => (
                                    <li key={i} className="choice-item" style={{ marginBottom: 8, borderRadius: 6, padding: '6px 0' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'default', fontSize: 16, gap: 10 }}>
                                        <input
                                          type="radio"
                                          name={`singleChoice_${q._id || idx}`}
                                          style={{ marginRight: 12, width: 18, height: 18 }}
                                          disabled
                                        />
                                        <span>{c}</span>
                                      </label>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Multiple Choice */}
                            {q.type === 'multipleChoice' && (
                              <div>
                                <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                                <ul className="choice-list" style={{ listStyle: 'none', paddingLeft: 0 }}>
                                  {(q.choices || []).map((c, i) => (
                                    <li key={i} className="choice-item" style={{ marginBottom: 4, borderRadius: 4, padding: '2px 0' }}>
                                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'default', fontSize: 16, gap: 10 }}>
                                        <input 
                                          type="checkbox" 
                                          name={`multipleChoice${q._id}`} 
                                          style={{ marginRight: 12, width: 18, height: 18 }} 
                                          disabled
                                        />
                                        <span>{c}</span>
                                      </label>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* Open Answer */}
                            {q.type === 'openAnswer' && (
                              <div>
                                <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                                {q.responseType === 'Number-Float' ? (
                                  <input 
                                    type="number" 
                                    step="any" 
                                    style={{ width: 200, padding: 4, backgroundColor: '#f0f0f0', borderRadius: 4, border: '1px solid #ccc' }} 
                                    disabled
                                  />
                                ) : (
                                  <textarea 
                                    rows={3} 
                                    style={{ 
                                      width: '100%', 
                                      padding: 8, 
                                      backgroundColor: '#f0f0f0', 
                                      borderRadius: 4, 
                                      border: '1px solid #ccc',
                                      resize: 'vertical'
                                    }} 
                                    disabled
                                  />
                                )}
                              </div>
                            )}
                            
                            {/* Upload */}
                            {q.type === 'upload' && (
                              <div>
                                <div className="rich-text-display" dangerouslySetInnerHTML={{ __html: q.label }} style={{ marginBottom: 8 }} />
                                <input 
                                  type="file" 
                                  style={{ 
                                    width: '100%', 
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    backgroundColor: '#f0f0f0'
                                  }} 
                                  disabled
                                />
                              </div>
                            )}
                          </div>
                        </li>
                        );
                      })}
                    </ol>
                  </div>
                )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <button
            type="button"
            className="ab-save-fab"
            style={{
              position: 'fixed',
              right: 'max(20px, env(safe-area-inset-right, 0px))',
              bottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
              fontSize: '16px',
              fontWeight: 600,
              padding: '14px 28px',
              border: 'none',
              borderRadius: 14,
              color: '#fff',
              cursor: submitting ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg, #1D4E89 0%, #2563a8 100%)',
              boxShadow: '0 10px 28px rgba(29, 78, 137, 0.38), 0 2px 8px rgba(0, 0, 0, 0.08)',
              opacity: (
                assignment.initialdirections.length === 0 ||
                assignment.questions.length === 0 ||
                (assignment.gradingresulttype === 'Points' && (!assignment.maxpoints || assignment.maxpoints <= 0)) ||
                !assignment.title.trim() ||
                !assignment.concentration
              )
                ? 0.45
                : 1,
              transition: 'transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease'
            }}
            disabled={
              assignment.initialdirections.length === 0 ||
              assignment.questions.length === 0 ||
              (assignment.gradingresulttype === 'Points' && (!assignment.maxpoints || assignment.maxpoints <= 0)) ||
              !assignment.title.trim() ||
              !assignment.concentration ||
              submitting
            }
            onClick={createAssignment}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 14px 32px rgba(29, 78, 137, 0.45), 0 4px 12px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = '0 10px 28px rgba(29, 78, 137, 0.38), 0 2px 8px rgba(0, 0, 0, 0.08)';
            }}
          >
            {submitting ? (isEditMode ? 'Saving…' : 'Creating…') : isEditMode ? 'Update' : 'Create'}
          </button>

          {/* Submission Loading Overlay */}
          {submitting && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.8)',
              zIndex: 9999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '20px',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                maxWidth: '400px',
                width: '90%'
              }}>
                {submissionStatus === 'submitting' && (
                  <>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '3px solid #f3f3f3',
                      borderTop: '3px solid #1D4E89',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      margin: '0 auto 16px auto'
                    }}></div>
                    <div style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>
                      {isEditMode ? 'Updating your assignment...' : 'Creating your assignment...'}
                    </div>
                  </>
                )}
                {submissionStatus === 'success' && (
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#28a745', textAlign: 'center' }}>
                    <div style={{ marginBottom: '16px' }}>
                      ✅ Assignment {isEditMode ? 'updated' : 'created'} successfully!
                    </div>
                    {!isEditMode && (
                      <button
                        onClick={() => window.location.reload()}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#1D4E89',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#0f3a6b'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#1D4E89'}
                      >
                        Make a new assignment?
                      </button>
                    )}
                                      {isEditMode && (
                    <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                      This popup will close automatically in a few seconds...
                    </div>
                  )}
                  </div>
                )}
                {submissionStatus === 'error' && (
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#dc3545' }}>
                    Error occurred. Please try again.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Uploads Modal */}
          {showUploadsModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1250
            }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '600px',
                maxHeight: '80vh',
                overflow: 'auto',
                width: '90%'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px'
                }}>
                  <h3 style={{ margin: 0, color: '#1D4E89' }}>
                    Select {directionDraft.element === 'image' ? 'Image' : 'Video'}
                  </h3>
                  <button
                    onClick={() => setShowUploadsModal(false)}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '24px',
                      cursor: 'pointer',
                      color: '#666'
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                  gap: '16px',
                  marginBottom: '20px'
                }}>
                  {userUploads.map((upload) => (
                    <div
                      key={upload.id}
                      onClick={() => {
                        if (directionDraft.element === 'image') {
                          setDirectionDraft(prev => ({
                            ...prev,
                            element: 'image',
                            value: `<img src="${upload.link}" width="250" height="250" alt="Image">`,
                            imageUrl: upload.link,
                            imageWidth: '250',
                            imageHeight: '250'
                          }));
                        } else {
                          setDirectionDraft(prev => ({
                            ...prev,
                            element: 'video',
                            value: upload.link
                          }));
                        }
                        setShowUploadsModal(false);
                      }}
                      style={{
                        position: 'relative',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#1D4E89';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e2e8f0';
                      }}
                    >
                      <UploadMediaOpenInNewTabLink href={upload.link} />
                      {directionDraft.element === 'image' ? (
                        <img 
                          src={upload.link} 
                          alt="Uploaded content"
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}
                        />
                      ) : (
                        <video
                          src={upload.link}
                          muted
                          playsInline
                          preload="metadata"
                          style={{
                            width: '100%',
                            height: '100px',
                            objectFit: 'cover',
                            borderRadius: '4px',
                            marginBottom: '8px'
                          }}
                        />
                      )}
                      <div style={{
                        fontSize: '12px',
                        color: '#666',
                        wordBreak: 'break-word'
                      }}>
                        {upload.filename || 'Untitled'}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <button
                    onClick={() => setShowUploadsModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {aiCapabilities?.UseAssistant === true && (
        <AssignmentAssistant
          assignment={assignment}
          setAssignment={setAssignment}
          selectedConcentrations={selectedConcentrations}
          pathwayRoots={pathwayRoots}
          taxonomyItems={taxonomyItems}
          scrollToAbSection={scrollToAbSection}
          aiCapabilities={aiCapabilities}
          onTaxonomyRefresh={refreshTaxonomyItems}
        />
        )}
        </>
      )}
    </div>
  );
}

export default AssignmentBuilder; 